import type { RequisitionDto } from '@eombor/contracts';

/**
 * YOZUVCHINING KELIB CHIQISHI — stansiya, korxona va ismi.
 *
 * Ro'yxat javobida hozircha bu maydonlar YO'Q: unda faqat
 * `requesterUserId` va `requesterOrgUnitId` keladi. Ular keyinroq
 * qo'shilishi kutilmoqda, lekin aynan qaysi NOM bilan kelishi hali aniq
 * emas (`stationName`, `station_name`, `station: { name }`, …). Shuning
 * uchun mijoz bir nechta mumkin bo'lgan nomni tekshiradi va topgan
 * birinchisini oladi. Backend maydonni qaytara boshlasa, kartochka uni
 * qo'shimcha o'zgarishsiz ko'rsatadi.
 *
 * Qiymat topilmasa — `null`. Chaqiruvchi bunday bedjni umuman chizmaydi:
 * bo'sh "—" nima demoqchi ekani noaniq bo'lardi.
 */

/** Nomli birlik: oddiy satr yoki `{ name }` / `{ shortName }` obyekti. */
export type NamedRef =
  | string
  | {
      name?: string | null;
      shortName?: string | null;
      short_name?: string | null;
      title?: string | null;
      code?: string | null;
    };

/** Yozuvchi: satr (ism) yoki uning stansiya/korxonasini ham olib keluvchi obyekt. */
export type RequesterRef =
  | string
  | {
      fullName?: string | null;
      full_name?: string | null;
      firstName?: string | null;
      first_name?: string | null;
      lastName?: string | null;
      last_name?: string | null;
      ism?: string | null;
      familiya?: string | null;
      username?: string | null;
      login?: string | null;
      station?: NamedRef | null;
      stationName?: string | null;
      station_name?: string | null;
      enterprise?: NamedRef | null;
      enterpriseName?: string | null;
      enterprise_name?: string | null;
      korxona?: NamedRef | null;
    };

/** Ro'yxat satri — server keyinroq qaytara boshlashi mumkin bo'lgan maydonlar bilan. */
export type RequisitionListItem = RequisitionDto & {
  station?: NamedRef | null;
  stationName?: string | null;
  station_name?: string | null;
  requesterStation?: NamedRef | null;
  requesterStationName?: string | null;
  enterprise?: NamedRef | null;
  enterpriseName?: string | null;
  enterprise_name?: string | null;
  korxona?: NamedRef | null;
  requesterEnterprise?: NamedRef | null;
  requesterEnterpriseName?: string | null;
  requesterName?: string | null;
  requesterFullName?: string | null;
  requester_full_name?: string | null;
  createdByName?: string | null;
  creatorName?: string | null;
  requesterUsername?: string | null;
  createdBy?: RequesterRef | null;
  created_by?: RequesterRef | null;
  creator?: RequesterRef | null;
  requester?: RequesterRef | null;
};

export interface RequesterOrigin {
  station: string | null;
  enterprise: string | null;
  creator: string | null;
}

type Bag = Record<string, unknown>;

const isBag = (value: unknown): value is Bag =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/* Obyektda QISQA belgi oldin: kartochkada "PCH-5" to'liq nomdan
   ko'ra tez o'qiladi (`useOrgUnits().shortNameOf` bilan bir xil qoida). */
const nameOf = (value: unknown): string | null => {
  if (!isBag(value)) return text(value);
  return (
    text(value['shortName']) ??
    text(value['short_name']) ??
    text(value['name']) ??
    text(value['title']) ??
    text(value['code'])
  );
};

const firstName = (bag: Bag, keys: readonly string[]): string | null => {
  for (const key of keys) {
    const found = nameOf(bag[key]);
    if (found !== null) return found;
  }
  return null;
};

const STATION_KEYS = [
  'stationName',
  'station_name',
  'requesterStationName',
  'requester_station_name',
  'station',
  'requesterStation',
  'requester_station',
] as const;

const ENTERPRISE_KEYS = [
  'enterpriseName',
  'enterprise_name',
  'requesterEnterpriseName',
  'requester_enterprise_name',
  'enterprise',
  'requesterEnterprise',
  'requester_enterprise',
  'korxona',
] as const;

/* YOZUVCHINING ISMI — uch pog'onada, birinchi topilgani olinadi:
   1) tayyor to'liq ism (`fullName` / `full_name`, tekis `requesterName` …);
   2) ism + familiya — bo'sh joy bilan birlashtiriladi;
   3) login (`username` / `login`).
   Pog'ona yozuvchi obyektlari bo'ylab TO'LIQ tekshiriladi: `creator` dagi
   to'liq ism `createdBy` dagi logindan ustun turadi. */
const CREATOR_FULL_NAME_KEYS = [
  'requesterFullName',
  'requester_full_name',
  'requesterName',
  'requester_name',
  'createdByFullName',
  'created_by_full_name',
  'createdByName',
  'created_by_name',
  'creatorFullName',
  'creator_full_name',
  'creatorName',
  'creator_name',
] as const;

const CREATOR_LOGIN_KEYS = [
  'requesterUsername',
  'requester_username',
  'createdByUsername',
  'created_by_username',
  'creatorUsername',
  'creator_username',
] as const;

const PERSON_KEYS = ['createdBy', 'created_by', 'creator', 'requester'] as const;

const FIRST_NAME_KEYS = ['firstName', 'first_name', 'ism'] as const;
const LAST_NAME_KEYS = ['lastName', 'last_name', 'familiya'] as const;

const firstText = (bag: Bag, keys: readonly string[]): string | null => {
  for (const key of keys) {
    const found = text(bag[key]);
    if (found !== null) return found;
  }
  return null;
};

/** Ism + familiya; bittasi bo'lsa — o'sha, ikkalasi ham bo'lmasa — `null`. */
const joinedName = (person: Bag): string | null => {
  const parts = [firstText(person, FIRST_NAME_KEYS), firstText(person, LAST_NAME_KEYS)];
  const joined = parts.filter((part) => part !== null).join(' ');
  return joined === '' ? null : joined;
};

const creatorOf = (bag: Bag): string | null => {
  /* Yozuvchi oddiy satr bo'lib kelsa (`createdBy: "Ali Valiyev"`) — u ism. */
  const plain = PERSON_KEYS.map((key) => text(bag[key])).find((name) => name !== null);
  const people = PERSON_KEYS.map((key) => bag[key]).filter(isBag);
  const fromPeople = (read: (person: Bag) => string | null): string | null => {
    for (const person of people) {
      const found = read(person);
      if (found !== null) return found;
    }
    return null;
  };

  return (
    firstText(bag, CREATOR_FULL_NAME_KEYS) ??
    fromPeople((person) => firstText(person, ['fullName', 'full_name'])) ??
    plain ??
    fromPeople(joinedName) ??
    firstText(bag, CREATOR_LOGIN_KEYS) ??
    fromPeople((person) => firstText(person, ['username', 'login']))
  );
};

export const requesterOriginOf = (row: RequisitionDto): RequesterOrigin => {
  const bag = row as unknown as Bag;
  /* Yozuvchi obyekti ichida ham qidiriladi: `createdBy: { station: … }`. */
  const people = PERSON_KEYS.map((key) => bag[key]).filter(isBag);

  const fromPeople = (keys: readonly string[]): string | null => {
    for (const person of people) {
      const found = firstName(person, keys);
      if (found !== null) return found;
    }
    return null;
  };

  return {
    station: firstName(bag, STATION_KEYS) ?? fromPeople(STATION_KEYS),
    enterprise: firstName(bag, ENTERPRISE_KEYS) ?? fromPeople(ENTERPRISE_KEYS),
    creator: creatorOf(bag),
  };
};
